import type { BrandDetails } from "schemas/generated/ai";

import { skipToken } from "@tanstack/react-query";
import { aiRouter, aiUtils } from "@v1/trpc/routes/ai";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";

const useBrandDetails = () => {
  const projectId = useCurrentProjectId();

  const { data, isLoading } = aiRouter.getBrandDetails.useQuery(
    projectId ? { projectId } : skipToken,
  );

  const utils = aiUtils;

  const brandDetails = data?.brandDetails;

  const { mutate } = aiRouter.createOrUpdateBrandDetails.useMutation({
    // Note (Evan, 2024-09-27): This is the react-query pattern for optimistic updates
    // see: https://tanstack.com/query/v4/docs/framework/react/guides/optimistic-updates
    onMutate: async ({ projectId, brandDetails: newBrandDetails }) => {
      // Note (Evan, 2024-09-24): Optimistically update, first cancel any outgoing refetches
      await utils.getBrandDetails.cancel({
        projectId,
      });

      // Capture the previous value to return as context
      const previousBrandDetails = utils.getBrandDetails.getData();

      // Optimistically update the cache
      utils.getBrandDetails.setData({ projectId }, () => ({
        brandDetails: newBrandDetails,
      }));

      return { previousBrandDetails };
    },
    // Note (Evan, 2024-09-24): On error, roll back the cache to the previous value
    onError: (_err, newBrandDetails, context) => {
      utils.getBrandDetails.setData(
        { projectId: newBrandDetails.projectId },
        context?.previousBrandDetails,
      );
    },
    onSettled: () => {
      void utils.getBrandDetails.invalidate();
    },
  });

  const setBrandDetails = (brandDetails: BrandDetails) => {
    if (!projectId) {
      return;
    }
    mutate({ projectId, brandDetails });
  };

  return { brandDetails, setBrandDetails, isLoading };
};

export default useBrandDetails;
